import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  Alert,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { useParams } from 'react-router-dom';
import React, { useContext, useState } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../contexts/user-context/user-context';
import {
  NotificationTemplate,
  OrderType,
  OrganisationSettings,
} from '../../../interfaces/delivery-company/orders';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import MultiPackageScanner, {
  timestampedMessage,
  validationResult,
} from '../../common/multi-package-scanner/multi-package-scanner';
import { displayLocation } from '../../../utils/locations';
import { GenericTableProps } from '../../common/generic-table/utils';
import {
  Decoding,
  DecodingWithOrderVal,
  generateUID,
  transformOrdersToDecodingWithOrderVal,
} from '../../../utils/encoding';
import SnackBar from '../../common/snack-bar/snack-bar';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import { CustomerWithIcon } from '../../common/styles/customer-with-icon';
import AddressWithIcon from '../../common/styles/address-with-icon';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { IOrderDeliveryTask } from '../../../interfaces/delivery-company/tasks';
import AddNotesDialog from '../common/add-notes-dialog';
import MarkAsFailedDialog from '../common/mark-as-failed-dialog';
import { displayMoney, get_symbol } from '../../../utils/money';
import { get_cod } from '../../../utils/cod';
import { DeliveryState, Workflow } from '../../../enums/orders';
import { useSnackBar } from '../../../contexts/snackbar';

interface DeliveryTaskPageProps {
  taskId: string;
  onTaskScan: (snackbarMsg?: timestampedMessage) => void;
  reloadTasks: () => void;
  showActions?: boolean;
}
function DeliveryTaskPage({
  taskId,
  onTaskScan,
  reloadTasks,
  showActions = true,
}: DeliveryTaskPageProps) {
  const { user, config } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [taskData, setTaskData] = useState<IOrderDeliveryTask>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] =
    React.useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = React.useState(false);
  const [snackBar, setSnackBar] = React.useState<timestampedMessage>();
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = React.useState(false);
  const [notificationTemplates, setNotificationTemplates] =
    React.useState<NotificationTemplate[]>();
  const [pendingBarcodes, setPendingBarcodes] = React.useState<string[]>([]);
  const { showMessage } = useSnackBar();

  const handleAddNoteMenuClick = () => {
    setAddNoteDialogOpen(true);
    handleMenuClose();
  };

  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${taskId}/`;
    const requestData = {
      notes: [template],
    };
    return axios.patch(endpoint, requestData, config);
  };

  const handleMarkAsFailedMenuClick = () => {
    setMarkAsFailedDialogOpen(true);
    handleMenuClose();
  };

  const handleMarkAsFailedTasks = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${taskId}/`;
    const requestData = {
      status: OrderDeliveryTaskState.FAILED,
      failure_reason: template,
    };

    return axios.patch(endpoint, requestData, config);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const completeDeliveryTask = async (
    orders: DecodingWithOrderVal[],
    setIsLoading: (isLoading: boolean) => void,
    setResponse: (resp?: AxiosResponse) => void,
    setMessage: (m: string) => void,
    config: any
  ) => {
    setIsLoading(true);
    setResponse(undefined);

    const temporary_packages = orders
      .filter((order) => order.type === 'temporary')
      .map((item) => item.id);

    if (taskData?.order.is_exchange && temporary_packages.length == 0) {
      showMessage('Please scan exchanged packages to proceed', 'error');
      setIsLoading(false);
      return;
    }
    try {
      const requestData = {
        status: OrderDeliveryTaskState.COMPLETED,
        pending_barcodes: pendingBarcodes,
      };
      const url = `${API_ENDPOINT}/api/v1/delivery-task/${taskId}/`;
      const newResponse = await axios.patch(url, requestData, config);
      setResponse(newResponse);
      if (response?.status === 200) {
        const newValue = {
          timestamp: Date.now(),
          content: 'Order Scanned',
          level: 'success',
        };
        onTaskScan(newValue as timestampedMessage);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(`Failed to complete task: ${e.response?.statusText}`);
      } else {
        setMessage('Failed to complete task.');
      }
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    const getAndSetTaskDetails = async () => {
      if (taskId !== undefined) {
        setIsLoading(true);
        try {
          const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${taskId}/`;
          const response = await axios.get<IOrderDeliveryTask>(
            endpoint,
            config
          );
          setResponse(response);
          setTaskData(response.data);
        } catch (e) {
          setTaskData(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load task data: ' + e.message);
            setResponse(e.response);
          } else {
            setMessage('Failed to load task data');
          }
          console.log(e);
        }
        setIsLoading(false);
      }
    };
    const fetchTemplates = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/settings`;
        const response = await axios.get<OrganisationSettings>(
          endpoint,
          config
        );
        setNotificationTemplates(response.data.notification_templates);
        console.log(response.data);
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          content: 'Failed to load notification templates',
          level: 'error',
        });
      }
    };
    getAndSetTaskDetails();
    fetchTemplates();
  }, [taskId]);

  let expectedPackages: DecodingWithOrderVal[] = [];

  if (taskData) {
    expectedPackages = transformOrdersToDecodingWithOrderVal([taskData.order]);
  }

  return (
    <Box sx={{ p: 2 }}>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <>
          {taskData ? (
            <>
              {showActions && (
                <>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      onClick={handleMenuClick}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      id="long-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => handleMarkAsFailedMenuClick()}>
                        Mark as Failed
                      </MenuItem>
                      <MenuItem onClick={() => handleAddNoteMenuClick()}>
                        Add Note
                      </MenuItem>
                    </Menu>
                  </div>
                  <br />{' '}
                </>
              )}

              {taskData.order.is_exchange && (
                <Alert severity="info">
                  Do not forget to scan exchange packages
                </Alert>
              )}
              <br />
              <MultiPackageScanner
                requirePendingBarcodes={taskData.order.is_exchange}
                onScanResultChanged={(orders) =>
                  setPendingBarcodes(
                    orders
                      .filter((item) => item.type === 'temporary')
                      .map((item) => item.id)
                  )
                }
                confirmationContent={
                  <>
                    {taskData.order.workflow === Workflow.STANDARD && (
                      <div>
                        <p style={{ fontWeight: 'bold' }}>
                          Delivered Packages:{' '}
                          {taskData.order.number_of_packages}
                        </p>
                        {taskData.order.is_exchange && (
                          <p style={{ fontWeight: 'bold' }}>
                            Collected Return Packages: {pendingBarcodes.length}
                          </p>
                        )}
                        <p style={{ fontWeight: 'bold' }}>
                          Collected Amount:{' '}
                          {displayMoney(get_cod(taskData.order))}
                        </p>
                      </div>
                    )}

                    {taskData.order.workflow === Workflow.RETURN && (
                      <div>
                        <p style={{ fontWeight: 'bold' }}>
                          Returned Packages: {taskData.order.number_of_packages}
                        </p>
                        <p style={{ fontWeight: 'bold' }}>You Paid: 0 $</p>
                      </div>
                    )}
                    {taskData.order.workflow == undefined && (
                      <Alert severity="error">ERROR: please contact us.</Alert>
                    )}
                  </>
                }
                confirmationContentSevereTargets={get_cod(taskData.order).map(
                  (item) => {
                    return {
                      label: `Amount in ${get_symbol(item[0])}`,
                      target:
                        taskData.order.workflow === Workflow.STANDARD
                          ? item[1].toString()
                          : '0',
                    };
                  }
                )}
                label={'package id'}
                acceptTemporaryOrders={taskData.order.is_exchange}
                validator={(decodingWithVal: DecodingWithOrderVal) => {
                  if (
                    decodingWithVal.order.delivery_state !==
                    DeliveryState.IN_TRANSIT
                  )
                    return {
                      validated: false,
                      error: 'order not assigned for delivery.',
                    };

                  return { validated: true };
                }}
                callback={completeDeliveryTask}
                actionType={'Complete Delivery'}
                expectedOrders={expectedPackages}
                getRenderOrdersProps={function (
                  decodings: DecodingWithOrderVal[]
                ): GenericTableProps<DecodingWithOrderVal> {
                  return {
                    sourceView: "delivery-task-page",
                    items: decodings,
                    fullWidth: true,
                    columns: [
                      {
                        id: 'order_id',
                        header: 'Order ID',
                        display_body: (decoding) => (
                          <>
                            {decoding.order.order_id}{' '}
                            {decoding.type === 'temporary' && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  justifyItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <HourglassBottomIcon fontSize="small" />
                                {decoding.id}
                              </div>
                            )}
                          </>
                        ),
                      },
                    ],
                    getItemUID: (decoding) => generateUID(decoding),
                    selectedItems: new Map(),
                    isLoading: false,
                  };
                }}
                allowUndoScan
              />
            </>
          ) : (
            <LinearProgress />
          )}
        </>
      )}
      {markAsFailedDialogOpen && (
        <MarkAsFailedDialog
          failure_reasons={[
            'Shop closed',
            'Wrong amount',
            'Wrong invoice',
            'Person absent',
            'Invoice lost',
          ]}
          onSuccess={(r) =>
            setTaskData({ ...taskData!, status: OrderDeliveryTaskState.FAILED })
          }
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleMarkAsFailedTasks}
          onClose={() => setMarkAsFailedDialogOpen(false)}
        />
      )}
      {addNoteDialogOpen && (
        <AddNotesDialog
          onSuccess={() => console.log('')}
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleAddNote}
          onClose={() => setAddNoteDialogOpen(false)}
        />
      )}
      {snackBar && snackBar.content && snackBar.content !== '' && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}
    </Box>
  );
}

export default DeliveryTaskPage;
